import { useToast } from '@chakra-ui/react'
import { faCopy } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import CopyTextWrapper from '../../../../../../components/CopyTextWrapper'
import DefaultModal from '../../../../../../components/DefaultModal'
import SumsubTransactionMonitoringStatusBadge from '../../../../../../components/SumsubTransactionMonitoringStatusBadge'
import Toast from '../../../../../../components/Toast'
import WalletSelector from '../../../../../../components/WalletSelector'
import {
  useCreateSystemWalletMutation,
  useGetAllWalletsQuery,
  useGetSwapQuery,
  useResendWalletVerificationMutation,
  useUpdateSwapMutation,
} from '../../../../../../redux/services/coreApi'
import { RootState } from '../../../../../../redux/store'
import type {
  SwapDto,
  SwapLockedVariable,
  SwapStatus,
  SwapUpdateStatusCommand,
  WalletDto,
  WalletHostingType,
  XToEurLockedVariable,
} from '../../../../../../types/coreApi-types'
import { formatNumber, formatUtcDate } from '../../../../../../utils/formatters'
import StatusBox from './StatusBox'
import SummaryRow from './SummaryRow'

interface TransactionSummaryModalProps {
  isOpen: boolean
  onClose: () => void
  activeSwap: SwapDto | null
  withdrawTransactionHash: string
  setWithdrawTransactionHash: (value: string) => void
  setSwapStatus: (command: SwapUpdateStatusCommand) => Promise<SwapDto>
  setSwapsStatusIsLoading: boolean
  adminNote: string
  setAdminNote: (val: string) => void
  setTempTransferStatus: (status: SwapStatus) => void
  openConfirmModal: () => void
  isReadOnly?: boolean
}

// Helper function to get wallet hosting type display
const getWalletHostingTypeDisplay = (
  hostingType?: WalletHostingType | null
): string => {
  if (!hostingType || hostingType === 'Unknown') {
    return 'Unknown'
  }
  if (hostingType === 'SelfHosted') {
    return 'Self Hosted'
  }
  if (hostingType === 'VaspHosted') {
    return 'Exchange Hosted'
  }
  return 'Unknown'
}

// Helper component for section headers
const SectionHeader: React.FC<{ title: string; color: string }> = ({
  title,
  color,
}) => (
  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
    <div className={`w-2 h-2 ${color} rounded-full`}></div>
    {title}
  </h3>
)

// Helper component for info rows
const InfoRow: React.FC<{
  label: string
  value: string | React.ReactNode
  isLast?: boolean
  warning?: boolean
  copy?: boolean
}> = ({ label, value, isLast = false, warning = false, copy = false }) => {
  const showCopy = copy && typeof value === 'string' && value !== 'N/A'

  return (
    <div
      className={`flex justify-between items-start gap-4 py-2 ${!isLast ? 'border-b border-gray-200 dark:border-gray-600' : ''}`}
    >
      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
        {label}
      </span>
      <div className="flex items-start gap-2 min-w-0">
        <span
          className={`text-sm font-mono text-right break-all ${
            warning
              ? 'text-amber-600 dark:text-amber-400 font-semibold'
              : 'text-gray-900 dark:text-gray-100'
          }`}
        >
          {value}
        </span>
        {showCopy && (
          <CopyTextWrapper text={value as string}>
            <span
              className="text-brand-accent dark:text-white cursor-pointer shrink-0"
              title="Copy to clipboard"
              aria-label="Copy to clipboard"
            >
              <FontAwesomeIcon icon={faCopy} />
            </span>
          </CopyTextWrapper>
        )}
      </div>
    </div>
  )
}

const TransactionSummaryModal: React.FC<TransactionSummaryModalProps> = ({
  isOpen,
  onClose,
  activeSwap,
  withdrawTransactionHash,
  setWithdrawTransactionHash,
  setSwapStatus,
  setSwapsStatusIsLoading,
  adminNote,
  setAdminNote,
  setTempTransferStatus,
  openConfirmModal,
  isReadOnly = false,
}) => {
  const toast = useToast()
  const userInfo = useSelector((state: RootState) => state.session.user)
  const [updateSwap, { isLoading: updateSwapIsLoading }] =
    useUpdateSwapMutation()
  const [createSystemWallet, { isLoading: createWalletIsLoading }] =
    useCreateSystemWalletMutation()
  const [resendWalletVerification, { isLoading: resendWalletVerificationIsLoading }] =
    useResendWalletVerificationMutation()

  const [saveMode, setSaveMode] = useState<
    'transaction-hash' | 'admin-note' | 'from-wallet' | null
  >(null)
  const [isCreatingWallet, setIsCreatingWallet] = useState(false)

  const [depositAmountChange, setDepositAmountChange] = useState(
    activeSwap?.deposit?.amount + ''
  )
  const [withdrawAmountChange, setWithdrawAmountChange] = useState(
    activeSwap?.withdraw?.amount + ''
  )

  const [swapRate, setSwapRate] = useState(activeSwap?.rate + '')

  const [lockedVariable, setLockedVariable] =
    useState<SwapLockedVariable | null>(null)

  const [xToEurLockedVariable, setXToEurLockedVariable] =
    useState<XToEurLockedVariable | null>(null)

  const [selectedFromWallet, setSelectedFromWallet] =
    useState<WalletDto | null>(null)
  const [walletAddressInput, setWalletAddressInput] = useState<string>('')
  const [isVaspHosted, setIsVaspHosted] = useState<boolean>(true)

  useEffect(() => {
    if (activeSwap?.deposit) {
      setDepositAmountChange(activeSwap.deposit.amount.toString())
    }
    if (activeSwap?.withdraw) {
      setWithdrawAmountChange(activeSwap.withdraw.amount.toString())
    }
    if (activeSwap?.rate) {
      setSwapRate(activeSwap.rate.toString())
    }
  }, [activeSwap])

  // Polling logic for review statuses - similar to StatusModal
  const { data: currentSwap, refetch: refetchSwap } = useGetSwapQuery(
    activeSwap?.id || '',
    {
      // Skip polling when modal is closed
      skip: !isOpen || !activeSwap?.id,
    }
  )

  // Use the current swap data if available, otherwise fall back to the prop
  const displaySwap = currentSwap || activeSwap

  // Fetch all system wallets for admin
  const { data: walletsData } = useGetAllWalletsQuery()

  // Wallet compatibility function
  const isWalletAndCryptoCurrencyPairCompatible = useMemo(
    () => (wallet: WalletDto, currency?: any) => {
      return Boolean(
        wallet &&
          wallet.network &&
          wallet.network?.cryptoCurrencies.find(x => x.id === currency?.id)
      )
    },
    []
  )

  // Get the current swap status from the display swap
  const currentSwapStatus = displaySwap?.swapStatus || null

  // Determine if we should poll based on current swap status
  const shouldPoll = currentSwapStatus === 'Review'

  // Handle polling updates
  useEffect(() => {
    if (shouldPoll && isOpen) {
      const interval = setInterval(() => {
        refetchSwap()
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [shouldPoll, isOpen, refetchSwap])

  // Computed values for amounts and fees
  const computedValues = useMemo(() => {
    const depositAmount = displaySwap?.deposit
      ? formatNumber(displaySwap.deposit.amount, displaySwap.deposit.currency)
      : ''

    const depositAmountInEur = displaySwap?.deposit
      ? `(${formatNumber(
          displaySwap.deposit.amount * displaySwap.deposit.currencyToEurRate,
          { currencyType: 'FiatCurrency', isoCode: 'EUR' }
        )})`
      : ''

    const withdrawAmount = displaySwap?.withdraw
      ? formatNumber(displaySwap.withdraw.amount, displaySwap.withdraw.currency)
      : ''

    const withdrawAmountInEur = displaySwap?.withdraw
      ? `(${formatNumber(
          displaySwap.withdraw.amount * displaySwap.withdraw.currencyToEurRate,
          { currencyType: 'FiatCurrency', isoCode: 'EUR' }
        )})`
      : ''

    const bankFee = displaySwap?.deposit ? displaySwap.bankFee * 100 : 0
    const bankFeeInEur = displaySwap?.deposit
      ? formatNumber(
          displaySwap.bankFee *
            displaySwap.deposit.amount *
            displaySwap.deposit.currencyToEurRate,
          { currencyType: 'FiatCurrency', isoCode: 'EUR' }
        )
      : 0

    const baseFee = displaySwap?.deposit ? displaySwap.baseFee * 100 : 0
    const baseFeeInEur = displaySwap?.deposit
      ? formatNumber(
          displaySwap.baseFee *
            displaySwap.deposit.amount *
            displaySwap.deposit.currencyToEurRate,
          { currencyType: 'FiatCurrency', isoCode: 'EUR' }
        )
      : 0

    const isWalletTransfer = Boolean(displaySwap?.withdraw?.toWallet)
    const isBankTransfer = Boolean(displaySwap?.withdraw?.toBankAccount)

    return {
      depositAmount,
      depositAmountInEur,
      withdrawAmount,
      withdrawAmountInEur,
      bankFee,
      bankFeeInEur,
      baseFee,
      baseFeeInEur,
      isWalletTransfer,
      isBankTransfer,
    }
  }, [displaySwap])

  const isDepositCrypto =
    displaySwap?.deposit?.currency.currencyType === 'CryptoCurrency'
  const isWithdrawCrypto =
    displaySwap?.withdraw?.currency.currencyType === 'CryptoCurrency'

  // For crypto transactions, we need to determine which leg is crypto and get its transaction hash
  const cryptoTxnId = isDepositCrypto
    ? (displaySwap?.deposit as any)?.transactionHash
    : displaySwap?.withdraw?.transactionHash

  // Determine if transaction hash should be editable based on swap status
  // Admins should always be allowed to update transaction hash unless status is Completed
  const isTransactionHashEditable = currentSwapStatus !== 'Completed'

  // Initialize selectedFromWallet with existing fromWallet and reset when swap changes
  useEffect(() => {
    if (displaySwap?.withdraw?.fromWallet) {
      setSelectedFromWallet(displaySwap.withdraw.fromWallet)
    } else {
      setSelectedFromWallet(null)
    }
  }, [displaySwap?.id, displaySwap?.withdraw?.fromWallet])

  // Function to create wallet from address input
  const handleCreateWalletFromAddress = async () => {
    if (!walletAddressInput.trim()) {
      toast({
        position: 'top',
        render: () => <Toast type="error">Please enter a wallet address</Toast>,
      })
      return
    }

    const inputAddress = walletAddressInput.trim()

    // First, check if a wallet with this address already exists in the dropdown
    const existingWallet = walletsData?.find(
      wallet =>
        wallet.address.toLowerCase() === inputAddress.toLowerCase() &&
        isWalletAndCryptoCurrencyPairCompatible(
          wallet,
          displaySwap?.withdraw?.currency
        )
    )

    if (existingWallet) {
      // Wallet already exists, just select and set it
      try {
        setIsCreatingWallet(true)

        // Set the existing wallet as selected
        setSelectedFromWallet(existingWallet)

        // Update the swap with the existing wallet
        if (displaySwap) {
          await setSwapStatus({
            swapId: displaySwap.id,
            status: currentSwapStatus,
            fromWalletId: existingWallet.id,
          })
        }

        // Clear the input
        setWalletAddressInput('')

        toast({
          position: 'top',
          render: () => (
            <Toast type="success">
              Existing wallet selected and set as from wallet
            </Toast>
          ),
        })
      } catch (error: any) {
        // Backend error handling will show appropriate toast automatically
      } finally {
        setIsCreatingWallet(false)
      }
      return
    }

    // Wallet doesn't exist, proceed with creation
    // Get network from the destination wallet or currency networks
    let network = null

    // First try to get network from the destination wallet
    if (displaySwap?.withdraw?.toWallet?.network) {
      network = displaySwap.withdraw.toWallet.network
    }
    // If not available, try to get from currency networks
    else if (displaySwap?.withdraw?.currency?.networks?.[0]) {
      network = displaySwap.withdraw.currency.networks[0]
    }

    if (!network) {
      // Let backend handle the error - it will show appropriate toast
      return
    }

    try {
      setIsCreatingWallet(true)

      // Create wallet name based on network and hosting type
      const networkName = network.name
      const companyName = isVaspHosted ? 'Kraken' : 'Penning'
      const walletName = `${companyName} ${networkName} Outbound`

      // Determine hosting type
      const hostingType: WalletHostingType = isVaspHosted
        ? 'VaspHosted'
        : 'SelfHosted'

      // Create the system wallet
      const newWallet = await createSystemWallet({
        name: walletName,
        address: inputAddress,
        destinationTag: null,
        networkId: network.id,
        hostingType,
      }).unwrap()

      // Set the new wallet as selected
      setSelectedFromWallet(newWallet)

      // Update the swap with the new from wallet
      if (displaySwap) {
        await setSwapStatus({
          swapId: displaySwap.id,
          status: currentSwapStatus,
          fromWalletId: newWallet.id,
        })
      }

      // Clear the input
      setWalletAddressInput('')

      toast({
        position: 'top',
        render: () => (
          <Toast type="success">Wallet created and set as from wallet</Toast>
        ),
      })
    } catch (error: any) {
      // Backend error handling will show appropriate toast automatically
    } finally {
      setIsCreatingWallet(false)
    }
  }

  // From wallet selection is no longer required for admins

  return (
    <DefaultModal
      isLoading={false}
      isOpen={isOpen}
      onClose={onClose}
      title="Transaction Summary"
      maxW="1000px"
    >
      <div className="p-6">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Transaction Details */}
          <div className="space-y-6">
            {/* Basic Transaction Info */}
            <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
              <SectionHeader
                title="Transaction Information"
                color="bg-blue-500"
              />
              <div className="space-y-0">
                {displaySwap?.createdAt && (
                  <InfoRow
                    label="Date"
                    value={formatUtcDate(displaySwap.createdAt)}
                  />
                )}
                <InfoRow
                  label="Unique ID"
                  value={displaySwap?.deposit?.bankDepositUniqueId || 'N/A'}
                  isLast
                />
              </div>
            </div>

            {/* Amounts & Rates */}
            <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
              <SectionHeader title="Amounts & Rates" color="bg-green-500" />
              <div className="space-y-4">
                <SummaryRow
                  title="Deposit amount"
                  value={`${computedValues.depositAmount} ${
                    displaySwap?.deposit?.currencyToEurRate === 0 ||
                    displaySwap?.deposit?.currency.isoCode === 'EUR'
                      ? ''
                      : computedValues.depositAmountInEur
                  }`}
                  modalEditable={{
                    title: `Change deposit amount`,
                    description: displaySwap?.deposit
                      ? `Change amount of ${
                          displaySwap.deposit.currency.isoCode
                        } received from ${formatNumber(
                          displaySwap.deposit.amount,
                          displaySwap.deposit.currency
                        )} to something else.`
                      : '',
                  }}
                  editable={{
                    isLoading: updateSwapIsLoading,
                    onClickSave: async () => {
                      if (displaySwap && lockedVariable) {
                        await updateSwap({
                          id: displaySwap.id,
                          amountIn: Number.parseFloat(depositAmountChange),
                          lockedVariable,
                          xToEurLockedVariable,
                        })
                      }
                    },
                    value: depositAmountChange,
                    onChangeInput: e => setDepositAmountChange(e.target.value),
                  }}
                  showLockedVariablePicker={true}
                  lockedVariable={lockedVariable}
                  setLockedVariable={setLockedVariable}
                  xToEurLockedVariable={xToEurLockedVariable}
                  setXToEurLockedVariable={setXToEurLockedVariable}
                  xToEurRateData={{
                    DepositToEurRate: {
                      currency: displaySwap?.deposit?.currency,
                      rate: displaySwap?.deposit?.currencyToEurRate,
                    },
                    WithdrawToEurRate: {
                      currency: displaySwap?.withdraw?.currency,
                      rate: displaySwap?.withdraw?.currencyToEurRate,
                    },
                  }}
                />
                <SummaryRow
                  title="Withdraw amount"
                  value={`${computedValues.withdrawAmount} ${
                    displaySwap?.deposit?.currencyToEurRate === 0 ||
                    displaySwap?.withdraw?.currency.isoCode === 'EUR'
                      ? ''
                      : computedValues.withdrawAmountInEur
                  }`}
                  modalEditable={{
                    title: `Change withdraw amount`,
                    description: displaySwap?.withdraw
                      ? `Change amount of ${
                          displaySwap.withdraw.currency.isoCode
                        } received from ${formatNumber(
                          displaySwap.withdraw.amount,
                          displaySwap.withdraw.currency
                        )} to something else.`
                      : '',
                  }}
                  editable={{
                    isLoading: updateSwapIsLoading,
                    onClickSave: async () => {
                      if (displaySwap && lockedVariable) {
                        await updateSwap({
                          id: displaySwap.id,
                          amountOut: Number.parseFloat(withdrawAmountChange),
                          lockedVariable,
                          xToEurLockedVariable,
                        })
                      }
                    },
                    value: withdrawAmountChange,
                    onChangeInput: e => setWithdrawAmountChange(e.target.value),
                  }}
                  showLockedVariablePicker={true}
                  lockedVariable={lockedVariable}
                  setLockedVariable={setLockedVariable}
                  xToEurLockedVariable={xToEurLockedVariable}
                  setXToEurLockedVariable={setXToEurLockedVariable}
                  xToEurRateData={{
                    DepositToEurRate: {
                      currency: displaySwap?.deposit?.currency,
                      rate: displaySwap?.deposit?.currencyToEurRate,
                    },
                    WithdrawToEurRate: {
                      currency: displaySwap?.withdraw?.currency,
                      rate: displaySwap?.withdraw?.currencyToEurRate,
                    },
                  }}
                />
                {displaySwap?.deposit && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 space-y-0">
                    <InfoRow
                      label="Bank fee"
                      value={`${computedValues.bankFee}% ${
                        displaySwap?.deposit.currencyToEurRate === 0
                          ? ''
                          : `(${computedValues.bankFeeInEur})`
                      }`}
                    />
                    <InfoRow
                      label="Base fee"
                      value={`${computedValues.baseFee}% ${
                        displaySwap?.deposit.currencyToEurRate === 0
                          ? ''
                          : `(${computedValues.baseFeeInEur})`
                      }`}
                      isLast
                    />
                  </div>
                )}
                <SummaryRow
                  title={`Rate (${displaySwap?.deposit?.currency.isoCode} -> ${displaySwap?.withdraw?.currency.isoCode})`}
                  value={`${displaySwap?.rate}`}
                  modalEditable={{
                    title: `Change rate`,
                    description: 'Change rate of the swap.',
                  }}
                  editable={{
                    isLoading: updateSwapIsLoading,
                    onClickSave: async () => {
                      if (displaySwap && lockedVariable) {
                        await updateSwap({
                          id: displaySwap.id,
                          rate: Number.parseFloat(swapRate),
                          lockedVariable,
                          xToEurLockedVariable,
                        })
                      }
                    },
                    value: swapRate,
                    onChangeInput: e => setSwapRate(e.target.value),
                  }}
                  showLockedVariablePicker={true}
                  lockedVariable={lockedVariable}
                  setLockedVariable={setLockedVariable}
                  xToEurLockedVariable={xToEurLockedVariable}
                  setXToEurLockedVariable={setXToEurLockedVariable}
                  xToEurRateData={{
                    DepositToEurRate: {
                      currency: displaySwap?.deposit?.currency,
                      rate: displaySwap?.deposit?.currencyToEurRate,
                    },
                    WithdrawToEurRate: {
                      currency: displaySwap?.withdraw?.currency,
                      rate: displaySwap?.withdraw?.currencyToEurRate,
                    },
                  }}
                />
                {(displaySwap?.deposit?.currency.isoCode !== 'EUR' ||
                  displaySwap?.withdraw?.currency.isoCode !== 'EUR') && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 space-y-0">
                    {displaySwap?.deposit?.currency.isoCode !== 'EUR' && (
                      <InfoRow
                        label={`Rate (${displaySwap?.deposit?.currency.isoCode} → EUR)`}
                        value={
                          displaySwap?.deposit?.currencyToEurRate?.toString() ||
                          'N/A'
                        }
                        isLast={
                          displaySwap?.withdraw?.currency.isoCode === 'EUR'
                        }
                      />
                    )}
                    {displaySwap?.withdraw?.currency.isoCode !== 'EUR' && (
                      <InfoRow
                        label={`Rate (${displaySwap?.withdraw?.currency.isoCode} → EUR)`}
                        value={
                          displaySwap?.withdraw?.currencyToEurRate?.toString() ||
                          'N/A'
                        }
                        isLast
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Destination & Controls */}
          <div className="space-y-6">
            {/* Wallet Verification — only shown when at least one leg has an outstanding
                Sumsub wallet-ownership-verification link (i.e. swap is on hold waiting on
                the user to prove they own the counterparty wallet). */}
            {displaySwap &&
              (displaySwap.deposit?.sumsubWalletVerificationUrl ||
                displaySwap.withdraw?.sumsubWalletVerificationUrl) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <SectionHeader
                    title="Wallet Verification"
                    color="bg-amber-500"
                  />
                  <p className="text-xs text-gray-700 dark:text-gray-300 mb-3">
                    This swap is waiting for the customer to verify wallet
                    ownership via Sumsub. If the existing link has expired,
                    click below to generate a fresh link and email it to the
                    customer.
                  </p>
                  <div className="space-y-2 mb-3">
                    {displaySwap.deposit?.sumsubWalletVerificationUrl && (
                      <div className="text-xs">
                        <div className="font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Deposit –{' '}
                          {displaySwap.deposit.sumsubOnHoldReason ||
                            'Wallet ownership verification required'}
                        </div>
                        <a
                          href={displaySwap.deposit.sumsubWalletVerificationUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 dark:text-blue-400 underline break-all"
                        >
                          {displaySwap.deposit.sumsubWalletVerificationUrl}
                        </a>
                      </div>
                    )}
                    {displaySwap.withdraw?.sumsubWalletVerificationUrl && (
                      <div className="text-xs">
                        <div className="font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Withdraw –{' '}
                          {displaySwap.withdraw.sumsubOnHoldReason ||
                            'Wallet ownership verification required'}
                        </div>
                        <a
                          href={
                            displaySwap.withdraw.sumsubWalletVerificationUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 dark:text-blue-400 underline break-all"
                        >
                          {displaySwap.withdraw.sumsubWalletVerificationUrl}
                        </a>
                      </div>
                    )}
                  </div>
                  <button
                    disabled={resendWalletVerificationIsLoading || isReadOnly}
                    onClick={async () => {
                      try {
                        await resendWalletVerification({
                          swapId: displaySwap.id,
                        }).unwrap()
                        toast({
                          position: 'top',
                          render: () => (
                            <Toast type="success">
                              New wallet verification link generated and
                              emailed to the customer
                            </Toast>
                          ),
                        })
                      } catch (e: any) {
                        toast({
                          position: 'top',
                          render: () => (
                            <Toast type="error">
                              {e?.data?.message ||
                                'Failed to resend wallet verification'}
                            </Toast>
                          ),
                        })
                      }
                    }}
                    className="small-button bg-brand-app-menu text-white"
                  >
                    {resendWalletVerificationIsLoading
                      ? 'Sending...'
                      : 'Resend wallet verification'}
                  </button>
                </div>
              )}

            {/* Sumsub Status Card */}
            <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
              <SectionHeader title="Sumsub Status" color="bg-indigo-500" />
              <div className="space-y-3">
                {displaySwap?.deposit && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Deposit Status
                    </span>
                    <SumsubTransactionMonitoringStatusBadge
                      status={
                        displaySwap.deposit.sumsubTransactionMonitoringStatus
                      }
                    />
                  </div>
                )}
                {displaySwap?.withdraw && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Withdraw Status
                    </span>
                    <SumsubTransactionMonitoringStatusBadge
                      status={
                        displaySwap.withdraw.sumsubTransactionMonitoringStatus
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Source Information */}
            {(displaySwap?.deposit?.fromWallet ||
              displaySwap?.deposit?.fromBankAccount) && (
              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader title="Source Information" color="bg-teal-500" />
                <div className="space-y-0">
                  {displaySwap?.deposit?.fromWallet && (
                    <>
                      <InfoRow
                        label="Address"
                        value={
                          displaySwap?.deposit?.fromWallet?.address || 'N/A'
                        }
                      />
                      {displaySwap?.deposit?.fromWallet?.destinationTag && (
                        <InfoRow
                          label="Destination tag"
                          value={displaySwap?.deposit?.fromWallet?.destinationTag}
                        />
                      )}
                      <InfoRow
                        label="Network"
                        value={
                          displaySwap?.deposit?.fromWallet?.network?.name || 'N/A'
                        }
                      />
                      <InfoRow
                        label="Hosting Type"
                        value={getWalletHostingTypeDisplay(
                          displaySwap?.deposit?.fromWallet?.hostingType
                        )}
                        isLast
                      />
                    </>
                  )}
                  {displaySwap?.deposit?.fromBankAccount && (
                    <>
                      {displaySwap?.deposit?.fromBankAccount?.name && (
                        <InfoRow
                          label="Bank Account"
                          value={displaySwap?.deposit?.fromBankAccount?.name}
                        />
                      )}
                      {displaySwap?.deposit?.fromBankAccount?.bankAccountDetails
                        ?.recipientName && (
                        <InfoRow
                          label="Account Holder"
                          value={
                            displaySwap?.deposit?.fromBankAccount
                              ?.bankAccountDetails?.recipientName
                          }
                        />
                      )}
                      {displaySwap?.deposit?.fromBankAccount?.bankAccountDetails
                        ?.accountNumber && (
                        <>
                          <InfoRow
                            label="Registration Nr."
                            value={
                              displaySwap?.deposit?.fromBankAccount
                                ?.bankAccountDetails?.registration || 'N/A'
                            }
                          />
                          <InfoRow
                            label="Account Nr."
                            value={
                              displaySwap?.deposit?.fromBankAccount
                                ?.bankAccountDetails?.accountNumber
                            }
                          />
                        </>
                      )}
                      {displaySwap?.deposit?.fromBankAccount?.bankAccountDetails
                        ?.iban && (
                        <>
                          <InfoRow
                            label="BIC"
                            value={
                              displaySwap?.deposit?.fromBankAccount
                                ?.bankAccountDetails?.bic || 'N/A'
                            }
                          />
                          <InfoRow
                            label="IBAN"
                            value={
                              displaySwap?.deposit?.fromBankAccount
                                ?.bankAccountDetails?.iban
                            }
                            isLast
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Destination Information */}
            {(computedValues.isWalletTransfer ||
              computedValues.isBankTransfer) && (
              <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4">
                <SectionHeader
                  title="Destination Information"
                  color="bg-purple-500"
                />
                <div className="space-y-0">
                  {computedValues.isWalletTransfer && (
                    <>
                      <InfoRow
                        label="Address"
                        value={
                          displaySwap?.withdraw?.toWallet?.address || 'N/A'
                        }
                        copy
                      />
                      {displaySwap?.withdraw?.toWallet?.destinationTag && (
                        <InfoRow
                          label="Destination tag"
                          value={
                            displaySwap?.withdraw?.toWallet?.destinationTag
                          }
                        />
                      )}
                      <InfoRow
                        label="Network"
                        value={
                          displaySwap?.withdraw?.toWallet?.network?.name ||
                          'N/A'
                        }
                      />
                      <InfoRow
                        label="Hosting Type"
                        value={getWalletHostingTypeDisplay(
                          displaySwap?.withdraw?.toWallet?.hostingType
                        )}
                        isLast
                      />
                    </>
                  )}
                  {displaySwap?.withdraw?.toBankAccount?.bankAccountDetails
                    .accountNumber && (
                    <>
                      <InfoRow
                        label="Registration Nr."
                        value={
                          displaySwap?.withdraw?.toBankAccount
                            ?.bankAccountDetails.registration || 'N/A'
                        }
                        copy
                      />
                      <InfoRow
                        label="Account Nr."
                        value={
                          displaySwap?.withdraw?.toBankAccount
                            ?.bankAccountDetails.accountNumber
                        }
                        copy
                      />
                    </>
                  )}

                  {displaySwap?.withdraw?.toBankAccount?.bankAccountDetails
                    .iban && (
                    <>
                      <InfoRow
                        label="BIC"
                        value={
                          displaySwap?.withdraw?.toBankAccount
                            ?.bankAccountDetails.bic || 'N/A'
                        }
                        copy
                      />
                      <InfoRow
                        label="IBAN"
                        value={
                          displaySwap?.withdraw?.toBankAccount
                            ?.bankAccountDetails.iban
                        }
                        copy
                      />
                      {displaySwap?.withdraw?.toBankAccount?.bankAccountDetails
                        .code && (
                        <InfoRow
                          label="Code"
                          value={
                            displaySwap?.withdraw?.toBankAccount
                              ?.bankAccountDetails.code
                          }
                          warning
                          copy
                          isLast
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Admin Controls - Full Width */}
        <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4 mt-4">
          <SectionHeader title="Admin Controls" color="bg-orange-500" />
          <div
            className={`grid grid-cols-1 gap-6 ${
              isWithdrawCrypto ? 'lg:grid-cols-2' : ''
            }`}
          >
            {/* From Wallet Selection - Only for crypto withdraws */}
            {isWithdrawCrypto && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    From Wallet
                  </label>
                </div>

                {/* Wallet Dropdown */}
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <WalletSelector
                        label=""
                        currency={displaySwap?.withdraw?.currency}
                        wallet={selectedFromWallet}
                        setWallet={setSelectedFromWallet}
                        walletsData={walletsData}
                        isWalletAndCryptoCurrencyPairCompatible={
                          isWalletAndCryptoCurrencyPairCompatible
                        }
                        disabled={!isWithdrawCrypto}
                        showAddWalletOption={true}
                      />
                    </div>
                    {selectedFromWallet && (
                      <button
                        disabled={
                          selectedFromWallet?.id ===
                            displaySwap?.withdraw?.fromWallet?.id ||
                          (setSwapsStatusIsLoading &&
                            saveMode === 'from-wallet')
                        }
                        onClick={async () => {
                          if (!displaySwap) return
                          setSaveMode('from-wallet')
                          await setSwapStatus({
                            swapId: displaySwap.id,
                            status: currentSwapStatus,
                            fromWalletId: selectedFromWallet?.id || null,
                          })
                          toast({
                            position: 'top',
                            render: () => (
                              <Toast type="success">
                                From wallet updated
                              </Toast>
                            ),
                          })
                        }}
                        className="small-button bg-brand-app-menu text-white"
                      >
                        {setSwapsStatusIsLoading &&
                        saveMode === 'from-wallet'
                          ? 'Saving...'
                          : 'Save Selected'}
                      </button>
                    )}
                  </div>
                  {selectedFromWallet && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Hosting Type: </span>
                      {getWalletHostingTypeDisplay(
                        selectedFromWallet.hostingType
                      )}
                    </div>
                  )}
                </div>

                {/* Quick Address Input */}
                <div className="mb-3">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                    Or paste wallet address to create new wallet
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={walletAddressInput}
                      onChange={e => setWalletAddressInput(e.target.value)}
                      placeholder="Paste wallet address here..."
                      className="border-0 text-xs outline-0 bg-white dark:bg-gray-600 h-[30px] rounded focus:outline-0 flex-1 px-2"
                    />
                    <button
                      disabled={isCreatingWallet}
                      onClick={handleCreateWalletFromAddress}
                      className="small-button bg-brand-app-menu text-white"
                    >
                      {isCreatingWallet ? 'Creating...' : 'Create & Set'}
                    </button>
                  </div>
                </div>

                {/* VASP Hosting Type Selection */}
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="vasp-hosted"
                      checked={isVaspHosted}
                      onChange={e => setIsVaspHosted(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="vasp-hosted"
                      className="text-xs font-medium text-gray-600 dark:text-gray-400"
                    >
                      VASP-Hosted
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction Hash & Admin Notes */}
            <div className="space-y-3">
              {/* Transaction Hash */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                  Transaction Hash
                </label>
                {isTransactionHashEditable ? (
                  <div className="flex items-center gap-2">
                    <input
                      onChange={e =>
                        setWithdrawTransactionHash(e.target.value)
                      }
                      placeholder="Enter transaction hash"
                      type="text"
                      value={withdrawTransactionHash}
                      className="border-0 text-xs outline-0 bg-white dark:bg-gray-600 h-[30px] rounded focus:outline-0 flex-1 px-2"
                    />
                    <button
                      disabled={
                        cryptoTxnId === withdrawTransactionHash ||
                        (setSwapsStatusIsLoading &&
                          saveMode === 'transaction-hash')
                      }
                      onClick={async () => {
                        if (!displaySwap) return
                        setSaveMode('transaction-hash')
                        await setSwapStatus({
                          swapId: displaySwap.id,
                          status: currentSwapStatus,
                          transactionHash: withdrawTransactionHash,
                        })
                      }}
                      className="small-button bg-brand-app-menu text-white"
                    >
                      {setSwapsStatusIsLoading &&
                      saveMode === 'transaction-hash'
                        ? 'Saving...'
                        : 'Save'}
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                    {cryptoTxnId || 'No transaction hash set'}
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                  Admin Notes
                </label>
                <div className="flex items-end gap-2">
                  <textarea
                    value={adminNote}
                    onChange={e => setAdminNote(e.target.value)}
                    placeholder="Enter admin notes..."
                    className="border-0 text-xs outline-0 bg-white dark:bg-gray-600 min-h-[50px] rounded focus:outline-0 p-2 resize-none flex-1"
                  />
                  <button
                    disabled={
                      adminNote === displaySwap?.adminNote ||
                      (setSwapsStatusIsLoading && saveMode === 'admin-note')
                    }
                    onClick={async () => {
                      if (!displaySwap || !displaySwap?.withdraw) return

                      try {
                        setSaveMode('admin-note')
                        await setSwapStatus({
                          swapId: displaySwap?.id,
                          status: currentSwapStatus,
                          adminNote: adminNote,
                        })
                        toast({
                          position: 'top',
                          render: () => (
                            <Toast type="success">Admin note updated</Toast>
                          ),
                        })
                      } catch (error) {}
                    }}
                    className="small-button bg-brand-app-menu text-white"
                  >
                    {setSwapsStatusIsLoading && saveMode === 'admin-note'
                      ? 'Saving...'
                      : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Controls - Full Width */}
        <div className="bg-brand-light-card dark:bg-brand-dark-card rounded-lg p-4 mt-4">
          <SectionHeader title="Change Status" color="bg-indigo-500" />
          <div className="grid grid-cols-7 gap-3">
            <StatusBox
              transferStatus="Review"
              isActive={currentSwapStatus === 'Review'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('Review')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="DepositPending"
              isActive={currentSwapStatus === 'DepositPending'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('DepositPending')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="DepositReceived"
              isActive={currentSwapStatus === 'DepositReceived'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('DepositReceived')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="WithdrawPending"
              isActive={currentSwapStatus === 'WithdrawPending'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('WithdrawPending')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="Completed"
              disabled={isReadOnly || currentSwapStatus !== 'WithdrawPending'}
              isActive={currentSwapStatus === 'Completed'}
              onClick={() => {
                setTempTransferStatus('Completed')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="OnHold"
              isActive={currentSwapStatus === 'OnHold'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('OnHold')
                openConfirmModal()
              }}
            />
            <StatusBox
              transferStatus="Cancelled"
              isActive={currentSwapStatus === 'Cancelled'}
              disabled={isReadOnly}
              onClick={() => {
                setTempTransferStatus('Cancelled')
                openConfirmModal()
              }}
            />
          </div>
        </div>
      </div>
    </DefaultModal>
  )
}

export default TransactionSummaryModal
